"use client";

import { c as _c } from "react/compiler-runtime";
import { api } from "@shared-server/api";
import { useLocalStorage } from "@shared-utils/hooks/use-local-storage";
import { BarChart3, ChevronLeft, ChevronRight } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Button } from "./shadcn/button";
import { Sheet, SheetContent, SheetTitle } from "./shadcn/sheet";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { MessagesCountPerChatWithFiles } from "@shared-components/core-metrics/messages-count-per-chat-with-files";
import { Tab, TabGroup } from "@shared-components/tab-group";
import { ProfilesContent, AiModelsCountInChats, PluginUsageChart, ActiveUsersPerTool, PluginsUsage, SummaryChatsAndImages, ResearchProjectsByType, ResearchCost } from "@shared-components/core-metrics";
import { z } from "zod";
import { PromptCountInChats } from "@shared-components/core-metrics/prompt-count-in-chats";
import type { GranularityType } from "@shared-server/api/routers/queries/metrics/utils";
import { DepartmentSelect } from "@shared-components/department-select";
import { DepartmentBadge } from "@shared-components/core-metrics/department-badge";
import { DepartmentStackProvider, useDepartmentStackContext } from "@shared-components/department-stack-provider";
const rangeSchema = z.enum(["all", "year", "month", "week"]);
type RangeType = z.infer<typeof rangeSchema>;
const rangesArray = rangeSchema.options;
const getRangeLabel = (range: RangeType): string => {
  return range;
};
const rangeOptions: Record<RangeType, GranularityType[]> = {
  all: ["day", "week", "month", "year"],
  year: ["day", "week", "month"],
  month: ["day", "week"],
  week: ["day"]
} as const;
const getGranularityOptions = (range: RangeType): GranularityType[] => {
  return rangeOptions[range] ?? rangeOptions["week"];
};
const getGranularityLabel = (granularity: GranularityType): string => {
  switch (granularity) {
    case "day":
      return "Daily";
    case "week":
      return "Weekly";
    case "month":
      return "Monthly";
    case "year":
      return "Yearly";
  }
};
export const CoreMetricsSheet = () => {
  const $ = _c(65);
  const isAdminQuery = api.admin.isAdmin.useQuery();
  const isAdmin = !isAdminQuery.isLoading && isAdminQuery.data;
  const [isOpen, setIsOpen] = useState(false);
  const [range, setRange] = useLocalStorage("core-metrics-range", "month");
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();
  const [offset, setOffset] = useState(0);
  const [granularity, setGranularity] = useLocalStorage("core-metrics-granularity", "day");
  let t0;
  let t1;
  if ($[0] !== offset || $[1] !== range) {
    t0 = () => {
      const date = new Date();
      bb6: switch (range) {
        case "all":
          {
            const allStart = new Date("2024-04-22T00:00:00.000Z");
            const allEnd = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999));
            setStartDate(allStart);
            setEndDate(allEnd);
            break bb6;
          }
        case "year":
          {
            date.setFullYear(date.getFullYear() + offset);
            const yearStart = new Date(Date.UTC(date.getFullYear(), 0, 1, 0, 0, 0, 0));
            const yearEnd = new Date(Date.UTC(date.getFullYear() + 1, 0, 0, 23, 59, 59, 999));
            setStartDate(yearStart);
            setEndDate(yearEnd);
            break bb6;
          }
        case "month":
          {
            date.setMonth(date.getMonth() + offset);
            const monthStart = new Date(Date.UTC(date.getFullYear(), date.getMonth(), 1, 0, 0, 0, 0));
            const monthEnd = new Date(Date.UTC(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59, 999));
            setStartDate(monthStart);
            setEndDate(monthEnd);
            break bb6;
          }
        case "week":
          {
            const today = new Date();
            today.setDate(today.getDate() + offset * 7);
            const dayOfWeek = today.getDay();
            const monday = new Date(today);
            monday.setDate(today.getDate() - (dayOfWeek + 6) % 7);
            const sunday = new Date(monday);
            sunday.setDate(monday.getDate() + 6);
            const weekStart = new Date(Date.UTC(monday.getFullYear(), monday.getMonth(), monday.getDate(), 0, 0, 0, 0));
            const weekEnd = new Date(Date.UTC(sunday.getFullYear(), sunday.getMonth(), sunday.getDate(), 23, 59, 59, 999));
            setStartDate(weekStart);
            setEndDate(weekEnd);
          }
      }
    };
    t1 = [offset, range];
    $[0] = offset;
    $[1] = range;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setOffset(0);
    };
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== range) {
    t3 = [range];
    $[5] = range;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[7] !== granularity || $[8] !== range || $[9] !== setGranularity) {
    t4 = () => {
      if (range === "week" && granularity !== "day") {
        setGranularity("day");
        return;
      }
      const options = getGranularityOptions(range);
      if (!options.includes(granularity) && options[0]) {
        setGranularity(options[0]);
      }
    };
    t5 = [granularity, range, setGranularity];
    $[7] = granularity;
    $[8] = range;
    $[9] = setGranularity;
    $[10] = t4;
    $[11] = t5;
  } else {
    t4 = $[10];
    t5 = $[11];
  }
  useEffect(t4, t5);
  if (!isAdmin) {
    return null;
  }
  let t6;
  if ($[12] !== isAdmin) {
    t6 = isAdmin && <Button className="px-2 hidden md:block" onClick={() => setIsOpen(true)} variant="ghost"><BarChart3 /></Button>;
    $[12] = isAdmin;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = value => setIsOpen(value);
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <VisuallyHidden.Root><SheetTitle>{"Core Metrics"}</SheetTitle></VisuallyHidden.Root>;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <h1 className="flex-none">{"Core Metrics"}</h1>;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <div className="flex flex-col gap-2 align-bottom">{t9}<div className="flex flex-col gap-2"><ParentStateNavigator /></div></div>;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const t11 = `${startDate?.toLocaleDateString()}-${endDate?.toLocaleDateString()}`;
  let t12;
  if ($[18] !== t11) {
    t12 = <span>{t11}</span>;
    $[18] = t11;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] !== offset) {
    t13 = () => setOffset(offset - 1);
    $[20] = offset;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ChevronLeft />;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t13) {
    t15 = <Button variant="secondary" onClick={t13}>{t14}</Button>;
    $[23] = t13;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== offset) {
    t16 = () => setOffset(offset + 1);
    $[25] = offset;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <ChevronRight />;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== t16) {
    t18 = <Button variant="secondary" onClick={t16}>{t17}</Button>;
    $[28] = t16;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] !== range || $[31] !== setRange) {
    t19 = rangesArray.map((rangeValue, index) => <Button key={index} variant={range === rangeValue ? "default" : "secondary"} onClick={() => setRange(rangeValue)}>{getRangeLabel(rangeValue)}</Button>);
    $[30] = range;
    $[31] = setRange;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== t12 || $[34] !== t15 || $[35] !== t18 || $[36] !== t19) {
    t20 = <div className="flex items-center gap-2">{t12}{t15}{t18}{t19}</div>;
    $[33] = t12;
    $[34] = t15;
    $[35] = t18;
    $[36] = t19;
    $[37] = t20;
  } else {
    t20 = $[37];
  }
  let t21;
  if ($[38] !== granularity || $[39] !== range || $[40] !== setGranularity) {
    let t22;
    if ($[42] !== granularity || $[43] !== setGranularity) {
      t22 = g => <Button key={g} variant={granularity === g ? "default" : "secondary"} onClick={() => setGranularity(g)}>{getGranularityLabel(g)}</Button>;
      $[42] = granularity;
      $[43] = setGranularity;
      $[44] = t22;
    } else {
      t22 = $[44];
    }
    t21 = getGranularityOptions(range).map(t22);
    $[38] = granularity;
    $[39] = range;
    $[40] = setGranularity;
    $[41] = t21;
  } else {
    t21 = $[41];
  }
  let t22;
  if ($[45] !== t21) {
    t22 = <div className="mt-2 flex justify-end gap-2">{t21}</div>;
    $[45] = t21;
    $[46] = t22;
  } else {
    t22 = $[46];
  }
  let t23;
  if ($[47] !== t20 || $[48] !== t22) {
    t23 = <div className="mt-4 flex justify-between">{t10}<div>{t20}{t22}</div></div>;
    $[47] = t20;
    $[48] = t22;
    $[49] = t23;
  } else {
    t23 = $[49];
  }
  let t24;
  if ($[50] !== endDate || $[51] !== granularity || $[52] !== startDate) {
    t24 = startDate && endDate && <TabGroup name="admin-feature-flags-tabs" defaultValue="Summary"><Tab key={0} label="Summary" className="-mr-12 max-h-[100vh] overflow-y-auto pb-85 pl-6 pr-6 pt-4"><SummaryChatsAndImages startDate={startDate} endDate={endDate} granularity={granularity} /></Tab><Tab key={1} label="Chat" className="-mr-12 pb-72 pl-6 pt-4"><div className="max-h-screen overflow-y-auto pb-40 pr-6"><MessagesCountPerChatWithFiles startDate={startDate} endDate={endDate} /><AiModelsCountInChats startDate={startDate} endDate={endDate} granularity={granularity} /><PluginUsageChart startDate={startDate} endDate={endDate} granularity={granularity} /><PromptCountInChats startDate={startDate} endDate={endDate} /></div></Tab><Tab key={2} label="Active Users" className="-mr-12 max-h-[100vh] overflow-y-auto pb-85 pl-6 pr-6 pt-4"><ActiveUsersPerTool startDate={startDate} endDate={endDate} granularity={granularity} /></Tab><Tab key={3} label="Profiles Content" className="-mr-12 h-[100vh] pl-6 pr-6 pt-4"><ProfilesContent startDate={startDate} endDate={endDate} granularity={granularity} /></Tab><Tab key={4} label="Plugins Usage" className="-mr-12 max-h-[100vh] overflow-y-auto pb-72 pl-6 pr-6 pt-4"><PluginsUsage startDate={startDate} endDate={endDate} /></Tab><Tab key={5} label="Research" className="-mr-12 max-h-[100vh] overflow-y-auto pb-72 pl-6 pr-6 pt-4"><ResearchProjectsByType startDate={startDate} endDate={endDate} granularity={granularity} /><ResearchCost startDate={startDate} endDate={endDate} granularity={granularity} /></Tab></TabGroup>;
    $[50] = endDate;
    $[51] = granularity;
    $[52] = startDate;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== t24) {
    t25 = <div className="-ml-6 mt-4 flex w-full flex-col gap-6">{t24}</div>;
    $[54] = t24;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== t23 || $[57] !== t25) {
    t26 = <SheetContent className="bg-panel-200"><DepartmentStackProvider>{t8}{t23}{t25}</DepartmentStackProvider></SheetContent>;
    $[56] = t23;
    $[57] = t25;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== isOpen || $[60] !== t26) {
    t27 = <Sheet open={isOpen} onOpenChange={t7}>{t26}</Sheet>;
    $[59] = isOpen;
    $[60] = t26;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== t27 || $[63] !== t6) {
    t28 = <>{t6}{t27}</>;
    $[62] = t27;
    $[63] = t6;
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  return t28;
};
const ParentStateNavigator = () => {
  const $ = _c(22);
  const {
    parentDepartmentId,
    pushParentDepartment,
    popParentDepartment,
    resetParentDepartment,
    selectedDepartmentName,
    setSelectedDepartmentName
  } = useDepartmentStackContext();
  const t0 = !parentDepartmentId;
  let t1;
  if ($[0] !== popParentDepartment || $[1] !== t0) {
    t1 = <Button onClick={popParentDepartment} disabled={t0} className="mb-4" size="xs">{"Go back to parent department"}</Button>;
    $[0] = popParentDepartment;
    $[1] = t0;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = !parentDepartmentId;
  let t3;
  if ($[3] !== resetParentDepartment || $[4] !== t2) {
    t3 = <Button size="xs" onClick={resetParentDepartment} disabled={t2} className="mb-4">{"Reset to top level"}</Button>;
    $[3] = resetParentDepartment;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t3) {
    t4 = <div className="flex gap-2">{t1}{t3}</div>;
    $[6] = t1;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== pushParentDepartment || $[10] !== resetParentDepartment || $[11] !== setSelectedDepartmentName) {
    t5 = department => {
      resetParentDepartment();
      setSelectedDepartmentName(department.name);
      pushParentDepartment(department.id);
    };
    $[9] = pushParentDepartment;
    $[10] = resetParentDepartment;
    $[11] = setSelectedDepartmentName;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== resetParentDepartment || $[14] !== selectedDepartmentName) {
    t6 = selectedDepartmentName !== null && <DepartmentBadge onClick={() => {
      resetParentDepartment();
    }}>{selectedDepartmentName}</DepartmentBadge>;
    $[13] = resetParentDepartment;
    $[14] = selectedDepartmentName;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t5 || $[17] !== t6) {
    t7 = <div className="flex gap-2 items-end"><DepartmentSelect onSelect={t5}>{t6}</DepartmentSelect></div>;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t4 || $[20] !== t7) {
    t8 = <>{t4}{t7}</>;
    $[19] = t4;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return t8;
};